import { useMemo, useState } from "react";
import { Checkbox, Segmented, Typography } from "antd";
import { TbChartBar, TbTable } from "react-icons/tb";
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatMoney } from "utils/functions";
import { MonthlyBreakdownTable } from "./CreditorBreakDownTable";

export type CreditorMonthlyWithhold = {
  creditor_name: string;
  funded_amount: number;
  daily_payment: number;
  monthly_breakdown: {
    month: string;
    monthly_revenue: number;
    monthly_payment_burden: number;
    withhold_percentage: number;
  }[];
};

type View = "chart" | "table";
type Props = { data: CreditorMonthlyWithhold[] };

const REVENUE_COLOR = "#5B8DEF";

/**
 * Each creditor gets its own distinct color.
 * This color is used for their Payment Burden line and their checkbox indicator.
 */
const CREDITOR_COLORS = [
  "#F5844C", // orange
  "#1DBE87", // green
  "#A855F7", // purple
  "#EF4444", // red
  "#F59E0B", // amber
  "#06B6D4", // cyan
  "#EC4899", // pink
  "#8B5CF6", // violet
];

function getCreditorColor(idx: number): string {
  return CREDITOR_COLORS[idx % CREDITOR_COLORS.length];
}

function yAxisFormatter(v: number): string {
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
}

interface TooltipEntry {
  dataKey: string;
  value: number;
  color: string;
}

function ChartTooltip({
  active,
  payload,
  label,
  creditorData,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
  creditorData: CreditorMonthlyWithhold[];
}) {
  if (!active || !payload?.length) return null;

  const revenueEntry = payload.find((e) => e.dataKey === "total_revenue");
  const burdenEntries = payload.filter((e) => e.dataKey !== "total_revenue" && e.value != null);

  return (
    <div
      className="bg-white rounded-lg"
      style={{
        border: "1px solid #e4e4e7",
        padding: "10px 14px",
        minWidth: 230,
        fontSize: 12,
      }}
    >
      <Typography.Text strong className="block mb-2" style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>

      {/* Total Revenue row */}
      {revenueEntry && revenueEntry.value != null && (
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: REVENUE_COLOR }} />
            <span style={{ color: "#71717a" }}>Total Revenue</span>
          </div>
          <span className="font-semibold" style={{ color: "#27272a" }}>
            {formatMoney(revenueEntry.value)}
          </span>
        </div>
      )}

      {/* Per-creditor burden rows */}
      {burdenEntries.length > 0 && (
        <>
          <div className="my-2" style={{ borderTop: "1px solid #f4f4f5" }} />
          <p
            className="mb-1"
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: "#a1a1aa",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Payment Burden
          </p>
          {burdenEntries.map((entry) => {
            const sep = entry.dataKey.indexOf("_");
            const creditorIdx = parseInt(entry.dataKey.slice(0, sep), 10);
            const creditor = creditorData[creditorIdx];
            if (!creditor) return null;
            return (
              <div key={entry.dataKey} className="flex items-center justify-between gap-4 mb-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: entry.color }} />
                  <span className="truncate" style={{ color: "#71717a", maxWidth: 160 }}>
                    {creditor.creditor_name}
                  </span>
                </div>
                <span className="font-semibold" style={{ color: "#27272a" }}>
                  {formatMoney(entry.value)}
                </span>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function SeriesLegendItem({ color, label, isArea = false }: { color: string; label: string; isArea?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <svg width="28" height="14">
        {isArea && <rect x="0" y="7" width="28" height="7" fill={color} opacity={0.25} rx={2} />}
        <line x1="0" y1="7" x2="28" y2="7" stroke={color} strokeWidth="2" />
      </svg>
      <Typography.Text style={{ fontSize: 12, color: "#71717a" }}>{label}</Typography.Text>
    </div>
  );
}

export function MultiCreditorMonthlyWithhold({ data }: Props) {
  const [view, setView] = useState<View>("chart");

  // All creditors visible by default
  const [visibleCreditors, setVisibleCreditors] = useState<Set<number>>(() => new Set(data.map((_, i) => i)));

  const toggleCreditor = (idx: number, checked: boolean) => {
    setVisibleCreditors((prev) => {
      const next = new Set(prev);
      checked ? next.add(idx) : next.delete(idx);
      return next;
    });
  };

  /**
   * Single time-axis across all creditors.
   * - total_revenue: sum of all creditors' revenue for that month (always shown)
   * - ${idx}_burden: per-creditor payment burden (toggled per creditor)
   */
  const chartData = useMemo(() => {
    const allMonths = [...new Set(data.flatMap((c) => c.monthly_breakdown.map((m) => m.month)))].sort();

    return allMonths.map((month) => {
      const point: Record<string, number | string | null> = { month };

      // Aggregate total revenue across all creditors
      let totalRevenue = 0;
      data.forEach((creditor) => {
        const bp = creditor.monthly_breakdown.find((m) => m.month === month);
        if (bp) totalRevenue += bp.monthly_revenue;
      });
      point.total_revenue = totalRevenue;

      // Per-creditor burden only
      data.forEach((creditor, idx) => {
        const bp = creditor.monthly_breakdown.find((m) => m.month === month);
        point[`${idx}_burden`] = bp ? bp.monthly_payment_burden : null;
      });

      return point;
    });
  }, [data]);

  return (
    <div className="flex flex-col gap-4">
      {/* ── Header ───────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Creditor Monthly Withhold
        </Typography.Title>
        <Segmented
          size="small"
          value={view}
          onChange={(v) => setView(v as View)}
          options={[
            { value: "chart", icon: <TbChartBar size={14} />, label: "Chart" },
            { value: "table", icon: <TbTable size={14} />, label: "Table" },
          ]}
        />
      </div>

      {/* ── Chart View ───────────────────────────────────── */}
      {view === "chart" && (
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 24, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={yAxisFormatter} tick={{ fontSize: 11 }} width={64} />
            <Tooltip
              content={(props) => (
                <ChartTooltip
                  active={props.active}
                  payload={props.payload as TooltipEntry[]}
                  label={props.label as string}
                  creditorData={data}
                />
              )}
            />

            {/* Total revenue — single area, always visible */}
            <Area
              dataKey="total_revenue"
              name="total_revenue"
              type="monotone"
              stroke={REVENUE_COLOR}
              fill={REVENUE_COLOR}
              fillOpacity={0.08}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              legendType="none"
            />

            {/* Per-creditor burden lines — toggled by checkboxes */}
            {data.flatMap((_, idx) => {
              if (!visibleCreditors.has(idx)) return [];
              return [
                <Line
                  key={`burden_${idx}`}
                  dataKey={`${idx}_burden`}
                  name={`${idx}_burden`}
                  type="monotone"
                  stroke={getCreditorColor(idx)}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                  connectNulls
                  legendType="none"
                />,
              ];
            })}
          </ComposedChart>
        </ResponsiveContainer>
      )}

      {/* ── Table View ───────────────────────────────────── */}
      {view === "table" && <MonthlyBreakdownTable data={data} />}

      {/* ── Legend + Creditor Filter (chart only) ────────── */}
      {view === "chart" && (
        <div className="flex flex-col gap-3 pt-3">
          {/* Series type legend */}
          <div className="flex items-center gap-5 flex-wrap">
            <SeriesLegendItem color={REVENUE_COLOR} label="Total Revenue" isArea />
            <div className="flex items-center gap-1.5">
              <svg width="28" height="14">
                <line x1="0" y1="7" x2="28" y2="7" stroke="#71717a" strokeWidth="2" strokeDasharray="4 2" />
              </svg>
              <Typography.Text style={{ fontSize: 12, color: "#71717a" }}>Payment Burden</Typography.Text>
            </div>
          </div>

          {/* Per-creditor checkboxes — color = creditor color = burden line color */}
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {data.map((creditor, idx) => (
              <Checkbox
                key={idx}
                checked={visibleCreditors.has(idx)}
                onChange={(e) => toggleCreditor(idx, e.target.checked)}
              >
                <span className="inline-flex items-center gap-2">
                  <svg width="22" height="12" style={{ flexShrink: 0 }}>
                    <line x1="0" y1="6" x2="22" y2="6" stroke={getCreditorColor(idx)} strokeWidth="2.5" />
                  </svg>
                  <Typography.Text
                    ellipsis={{ tooltip: creditor.creditor_name }}
                    style={{
                      fontSize: 13,
                      maxWidth: 200,
                      color: visibleCreditors.has(idx) ? "#3f3f46" : "#a1a1aa",
                    }}
                  >
                    {creditor.creditor_name}
                  </Typography.Text>
                </span>
              </Checkbox>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
